import React from 'react';

// Module Components
import AlignmentSection from './AlignmentSection';
import ButtonSection from './ButtonSection';
import CustomStylingSection from './CustomStylingSection';
import DescriptionStyleSection from './DescriptionStyleSection';
import ImageFitSection from './ImageFitSection';
import LabelSection from './LabelSection';
import PricingSection from './PricingSection';
import StrikethroughSection from './StrikethroughSection';

// Selectors
import { PropertySectionKind, selectPropertySections } from '../../../selectors';

// Constants
import {
  DEFAULT_CTA_CONFIGURATION,
  DEFAULT_DESCRIPTION_STYLE,
  DEFAULT_IMAGE_FIT,
  getFieldPanelName,
  getFieldSelector,
} from '../../../constants';

// Types
import {
  CtaConfiguration,
  DescriptionStyle,
  FieldAlignment,
  FieldDefinition,
  FieldConfiguration,
  ImageFit,
} from '../../../types';

interface FieldConfigurationPanelProps {
  definition: FieldDefinition;
  configuration: FieldConfiguration;
  onDisplayLabelChange: (displayLabel: string) => void;
  onAlignmentChange: (alignment: FieldAlignment) => void;
  onImageFitChange: (imageFit: ImageFit) => void;
  onDescriptionStyleChange: (descStyle: DescriptionStyle) => void;
  onShowUomChange: (showUom: boolean) => void;
  /** Per element, unlike onShowUomChange above it (14021). */
  onStrikethroughChange: (strikethrough: boolean) => void;
  onCtaOptionsChange: (options: Partial<CtaConfiguration>) => void;
  onCustomCssChange: (customCss: string) => void;
}

/**
 * The body of an element's properties panel: its sections, in the order its field
 * type declares them (spec R66).
 *
 * This is the registry ADR-2B asked for and ADR-4B widened — the one place that maps
 * a section kind to the component that renders it. The *membership* question ("does
 * List Price show Pricing?") is not asked here at all: `selectPropertySections`
 * answers it from `constants/PROPERTY_SECTIONS`, and this switch renders whatever
 * comes back. So adding a section for one field type cannot add, remove or reorder a
 * section for another, and no section component knows which field it is inside.
 *
 * Each new setting's default is applied at *read* time (`?? DEFAULT_…`), the same way
 * `imageFit` already was: a card stored before the setting existed keeps loading
 * unchanged (ADR-4C).
 */
const FieldConfigurationPanel = ({
  definition,
  configuration,
  onDisplayLabelChange,
  onAlignmentChange,
  onImageFitChange,
  onDescriptionStyleChange,
  onShowUomChange,
  onStrikethroughChange,
  onCtaOptionsChange,
  onCustomCssChange,
}: FieldConfigurationPanelProps) => {
  const renderSection = (kind: PropertySectionKind) => {
    switch (kind) {
      case 'label':
        return (
          <LabelSection
            key={kind}
            panelName={getFieldPanelName(definition.id)}
            displayLabel={configuration.displayLabel}
            onDisplayLabelChange={onDisplayLabelChange}
          />
        );
      case 'alignment':
        return (
          <AlignmentSection
            key={kind}
            fieldId={definition.id}
            fieldName={definition.name}
            alignment={configuration.alignment}
            onAlignmentChange={onAlignmentChange}
          />
        );
      case 'imageFit':
        return (
          <ImageFitSection
            key={kind}
            imageFit={configuration.imageFit ?? DEFAULT_IMAGE_FIT}
            onImageFitChange={onImageFitChange}
          />
        );
      case 'descriptionStyle':
        return (
          <DescriptionStyleSection
            key={kind}
            descStyle={configuration.descStyle ?? DEFAULT_DESCRIPTION_STYLE}
            onDescriptionStyleChange={onDescriptionStyleChange}
          />
        );
      case 'pricing':
        return (
          <PricingSection
            key={kind}
            showUom={configuration.showUom ?? false}
            onShowUomChange={onShowUomChange}
          />
        );
      case 'strikethrough':
        return (
          <StrikethroughSection
            key={kind}
            strikethrough={configuration.strikethrough ?? false}
            onStrikethroughChange={onStrikethroughChange}
          />
        );
      case 'button':
        return (
          <ButtonSection
            key={kind}
            cta={configuration.cta ?? DEFAULT_CTA_CONFIGURATION}
            onCtaOptionsChange={onCtaOptionsChange}
          />
        );
      case 'customStyling':
      default:
        return (
          <CustomStylingSection
            key={kind}
            fieldId={definition.id}
            selector={getFieldSelector(definition.id)}
            panelName={getFieldPanelName(definition.id)}
            customCss={configuration.customCss ?? ''}
            onCustomCssChange={onCustomCssChange}
          />
        );
    }
  };

  return <div>{selectPropertySections(definition.id).map(renderSection)}</div>;
};

export default FieldConfigurationPanel;
